import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { preAuthorizationService, type PreAuthType, type PreAuthServiceType } from '../../../services/preAuthorizationService';
import { patientInsuranceService } from '../../../services/insuranceService';
import { patientService } from '../../../services/patientService';
import apiClient from '../../../services/apiClient';
import { useAuth } from '../../../contexts/AuthContext';
import type { Doctor } from '../../../types/api';
import Breadcrumb from '../../../components/ui/Breadcrumb';
import Spinner from '../../../components/ui/Spinner';

const AUTH_TYPE_LABELS: Record<PreAuthType, string> = {
  consultation: 'Consulta',
  lab: 'Laboratorio',
  imaging: 'Imagen/Radiología',
  procedure: 'Procedimiento',
  medication: 'Medicamento',
  hospitalization: 'Hospitalización',
};

const SERVICE_TYPE_LABELS: Record<PreAuthServiceType, string> = {
  service: 'Servicio',
  lab_test: 'Prueba de lab',
  imaging_study: 'Estudio de imagen',
  medication: 'Medicamento',
  other: 'Otro',
};

interface PatientOption {
  id: number;
  first_name: string;
  last_name: string;
  medical_record_number: string;
}

interface InsuranceOption {
  id: number;
  policy_number: string;
  company?: { name: string };
  plan?: { name: string };
}

const PreAuthorizationFormPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const [patientSearch, setPatientSearch] = useState('');
  const [patientResults, setPatientResults] = useState<PatientOption[]>([]);
  const [searchingPatients, setSearchingPatients] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<PatientOption | null>(null);
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);

  const [insurances, setInsurances] = useState<InsuranceOption[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);

  const [form, setForm] = useState({
    patient_insurance_id: '',
    authorization_type: 'consultation' as PreAuthType,
    service_type: 'service' as PreAuthServiceType,
    service_name: '',
    quantity: 1,
    requested_by: '',
    notes: '',
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiClient.get('/appointments/doctors')
      .then((res: { data?: { data?: Doctor[] } }) => setDoctors(res.data?.data || []))
      .catch(() => { void 0; });

    const patientId = searchParams.get('patient_id');
    if (patientId) {
      patientService.getById(Number(patientId)).then((p: PatientOption) => {
        setSelectedPatient(p);
        setPatientSearch(`${p.first_name} ${p.last_name}`);
      }).catch(() => { void 0; });
    }

    if (user?.doctor?.id) {
      setForm(f => ({ ...f, requested_by: String(user.doctor.id) }));
    }
  }, []);

  useEffect(() => {
    if (selectedPatient) {
      patientInsuranceService.getByPatient(selectedPatient.id).then((ins) => {
        setInsurances(ins as InsuranceOption[]);
        if (ins.length === 1) {
          setForm(f => ({ ...f, patient_insurance_id: String((ins[0] as InsuranceOption).id) }));
        }
      }).catch(() => setInsurances([]));
    } else {
      setInsurances([]);
      setForm(f => ({ ...f, patient_insurance_id: '' }));
    }
  }, [selectedPatient]);

  const searchPatients = useCallback(async (term: string) => {
    if (!term || term.length < 2) { setPatientResults([]); return; }
    setSearchingPatients(true);
    try {
      const results = await patientService.search(term);
      setPatientResults((results || []) as PatientOption[]);
    } catch {
      setPatientResults([]);
    } finally {
      setSearchingPatients(false);
    }
  }, []);

  useEffect(() => {
    if (selectedPatient) return;
    const t = setTimeout(() => searchPatients(patientSearch), 300);
    return () => clearTimeout(t);
  }, [patientSearch, searchPatients, selectedPatient]);

  const handlePatientSelect = (p: PatientOption) => {
    setSelectedPatient(p);
    setPatientSearch(`${p.first_name} ${p.last_name}`);
    setShowPatientDropdown(false);
    setPatientResults([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPatient) { setError('Seleccione un paciente'); return; }
    if (!form.patient_insurance_id) { setError('Seleccione el seguro del paciente'); return; }
    if (!form.requested_by) { setError('Seleccione el médico solicitante'); return; }
    if (!form.service_name.trim()) { setError('Ingrese el nombre del servicio'); return; }

    setSubmitting(true);
    setError(null);
    try {
      const created = await preAuthorizationService.create({
        patient_id: selectedPatient.id,
        patient_insurance_id: Number(form.patient_insurance_id),
        authorization_type: form.authorization_type,
        service_type: form.service_type,
        service_name: form.service_name.trim(),
        quantity: form.quantity,
        requested_by: Number(form.requested_by),
        notes: form.notes || undefined,
      });
      navigate(`/pre-authorizations/${created.id}`);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? 'Error al crear la pre-autorización');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="page-header">
        <div className="row align-items-center">
          <div className="col-sm-12">
            <Breadcrumb items={[{ label: 'Inicio', to: '/' }, { label: 'Pre-Autorizaciones', to: '/pre-authorizations' }, { label: 'Nueva' }]} />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title mb-4">Nueva Pre-Autorización ARS</h4>
              {error && <div className="alert alert-danger">{error}</div>}

              <form onSubmit={handleSubmit}>
                <div className="row">
                  {/* Paciente */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Paciente <span className="text-danger">*</span></label>
                    <div className="position-relative">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Buscar paciente por nombre o cédula..."
                        value={patientSearch}
                        onChange={(e) => {
                          setPatientSearch(e.target.value);
                          setShowPatientDropdown(true);
                          if (selectedPatient) setSelectedPatient(null);
                        }}
                        onFocus={() => setShowPatientDropdown(true)}
                      />
                      {searchingPatients && (
                        <small className="ms-2"><Spinner text="Buscando..." /></small>
                      )}
                      {showPatientDropdown && patientResults.length > 0 && (
                        <div className="dropdown-menu show w-100" style={{ maxHeight: 200, overflowY: 'auto' }}>
                          {patientResults.map((p) => (
                            <button
                              key={p.id}
                              type="button"
                              className="dropdown-item"
                              onMouseDown={() => handlePatientSelect(p)}
                            >
                              {p.first_name} {p.last_name}
                              <small className="text-muted ms-2">#{p.medical_record_number}</small>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Seguro */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Seguro ARS <span className="text-danger">*</span></label>
                    <select
                      className="form-select"
                      value={form.patient_insurance_id}
                      onChange={(e) => setForm(f => ({ ...f, patient_insurance_id: e.target.value }))}
                      disabled={!selectedPatient}
                    >
                      <option value="">
                        {!selectedPatient ? '(Seleccione un paciente primero)' : insurances.length === 0 ? 'Sin seguros registrados' : 'Seleccionar seguro...'}
                      </option>
                      {insurances.map((ins) => (
                        <option key={ins.id} value={ins.id}>
                          {ins.company?.name ?? 'ARS'} — {ins.plan?.name ?? 'Plan'} ({ins.policy_number})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Tipo de autorización */}
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Tipo de Autorización <span className="text-danger">*</span></label>
                    <select
                      className="form-select"
                      value={form.authorization_type}
                      onChange={(e) => setForm(f => ({ ...f, authorization_type: e.target.value as PreAuthType }))}
                    >
                      {(Object.entries(AUTH_TYPE_LABELS) as [PreAuthType, string][]).map(([v, l]) => (
                        <option key={v} value={v}>{l}</option>
                      ))}
                    </select>
                  </div>

                  {/* Tipo de servicio */}
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Tipo de Servicio <span className="text-danger">*</span></label>
                    <select
                      className="form-select"
                      value={form.service_type}
                      onChange={(e) => setForm(f => ({ ...f, service_type: e.target.value as PreAuthServiceType }))}
                    >
                      {(Object.entries(SERVICE_TYPE_LABELS) as [PreAuthServiceType, string][]).map(([v, l]) => (
                        <option key={v} value={v}>{l}</option>
                      ))}
                    </select>
                  </div>

                  {/* Cantidad */}
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Cantidad</label>
                    <input
                      type="number"
                      className="form-control"
                      min={1}
                      value={form.quantity}
                      onChange={(e) => setForm(f => ({ ...f, quantity: Number(e.target.value) }))}
                    />
                  </div>

                  {/* Nombre del servicio */}
                  <div className="col-md-8 mb-3">
                    <label className="form-label">Nombre del Servicio / Procedimiento <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Ej: Consulta cardiología, Hemograma completo..."
                      value={form.service_name}
                      onChange={(e) => setForm(f => ({ ...f, service_name: e.target.value }))}
                    />
                  </div>

                  {/* Médico solicitante */}
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Médico Solicitante <span className="text-danger">*</span></label>
                    <select
                      className="form-select"
                      value={form.requested_by}
                      onChange={(e) => setForm(f => ({ ...f, requested_by: e.target.value }))}
                    >
                      <option value="">Seleccionar médico...</option>
                      {doctors.map((d) => (
                        <option key={d.id} value={d.id}>
                          Dr. {d.first_name} {d.last_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Notas */}
                  <div className="col-md-12 mb-3">
                    <label className="form-label">Notas</label>
                    <textarea
                      className="form-control"
                      rows={3}
                      placeholder="Indicaciones clínicas, observaciones..."
                      value={form.notes}
                      onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="d-flex gap-2 mt-2">
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? <Spinner size="sm" text="Guardando..." /> : 'Crear Pre-Autorización'}
                  </button>
                  <Link to="/pre-authorizations" className="btn btn-outline-secondary">Cancelar</Link>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PreAuthorizationFormPage;
